import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays, FileText, Fuel, Package, Plane, type LucideIcon } from "lucide-react";
import TasksSection from "./tasks-section";

// The documents a person starts themselves. How it is read: the tiles appear in
// this order, each leads to the cabinet section (/profile/<me>/<section>) where
// the document is filled in. The fuel tile is left out when the person has no use
// for fuel reports. A tile with a `search` opens the form of its section right away
// (the leave section reads ?new=1 and opens its request sheet).
const FILL_IN_TILES: {
  key: string;
  labelKey: string;
  section: string;
  search?: string;
  Icon: LucideIcon;
  isFuel: boolean;
}[] = [
  { key: "leave", labelKey: "tasks_fill_leave", section: "leaves", search: "?new=1", Icon: CalendarDays, isFuel: false },
  { key: "fuel", labelKey: "tasks_fill_fuel", section: "fuel-reports", Icon: Fuel, isFuel: true },
  { key: "trip", labelKey: "tasks_fill_trip", section: "business-trips", Icon: Plane, isFuel: false },
  { key: "asset", labelKey: "tasks_fill_asset", section: "assets", Icon: Package, isFuel: false },
  { key: "document", labelKey: "tasks_fill_document", section: "files", Icon: FileText, isFuel: false },
];

interface FillInTilesProps {
  companyId: string;
  userId: string;
  /** False when the summary says the person has no use for fuel reports. */
  hasFuel: boolean;
}

/** «Pildyti»: one tile per document the person can start. */
export default function FillInTiles({ companyId, userId, hasFuel }: FillInTilesProps) {
  const { t } = useTranslation();
  const tiles = FILL_IN_TILES.filter((tile) => hasFuel || !tile.isFuel);

  return (
    <TasksSection title={t("tasks_section_fill_in")}>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map(({ key, labelKey, section, search = "", Icon }) => (
          <li key={key}>
            <Link
              to={`/app/${companyId}/profile/${userId}/${section}${search}`}
              className="flex h-24 flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 text-center text-sm text-gray-900 hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <Icon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              {t(labelKey)}
            </Link>
          </li>
        ))}
      </ul>
    </TasksSection>
  );
}
